import {Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {ChartQuantity, CUSTOM_DATE_FORMATS} from '../economics-aggregated/ec-economics-aggregated.component';
import {I18NService} from '../../../i18n/i18n.service';
import {combineLatest, forkJoin, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {BillingPeriod, BillingPeriodConfig} from '../billing-filter/ec-billing-filter.component';
import {ECCacvRedistributionMode, ECEconomicsThirdPartyProducerPod, ECPlantTipMode, ECPlantTipType} from 'smag-commons/dto/eco';
import {ECO_ERROR_PARAM_KEY, EconomicsErrorType} from 'smag-commons/dto/eco/errors';
import {
    CACV_STRING,
    DataSourceInfo,
    DEFAULT_DIALOG_MAX_WIDTH,
    DEFAULT_DIALOG_MIN_WIDTH,
    DEFAULT_PAGE_SIZE,
    DEFAULT_PAGE_SIZES,
    disableDataLabels,
    disableLegend,
    generatesTip,
    getDataSourceIconInfo,
    getGridColor,
    getSelectionLineColor,
    getTimelyData,
    getTooltipBackgroundColor,
    getTooltipBorderColor,
    isEnergyCommunity,
    isSingleMonthSelected,
    mkBillingPeriodParams,
    noData,
    sanityFromTimeActivationDate,
    sanityToTime
} from '../../../shared/ec-economics';
import {isDefined} from 'src/app/commons/commons';
import {distinctUntilChanged, filter, first, map, shareReplay, switchMap, takeUntil} from 'rxjs/operators';
import {ConfigurationService} from '../../../shared/configuration.service';
import {Router} from '@angular/router';
import {can} from '../../../shared/permissions';
import {Permission} from 'smag-commons/dto/api';
import {SmagApiService} from '../../../api/smag-api.service';
import * as moment from 'moment-timezone';
import {hasError, showError} from 'src/app/shared/economics-errors';
import {ECEconomicsThirdPartyProducerPodTimelyData} from 'smag-commons/dto/eco/community';
import {EnergyCommunity, PlantIncentiveMode} from 'smag-commons/cfg/hierarchy';
import {mkMode} from '../../../shared/energy-community';
import {environment} from '../../../../environments/environment';
import {ApexTitleSubtitle} from 'ng-apexcharts';
import {NiceValuePipe} from '../../../shared/shared.pipe';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {TIME_DELTA_DAY, TIME_DELTA_HOUR} from '../../../shared/line-chart/line-chart.pipe';
import {SharedConfig} from '../../../shared/shared.config';
import {ResizeService} from '../../../shared/resize.service';
import {MatDialog} from '@angular/material/dialog';
import {ECMemberPOD, EnergyCommunityNamedPlant} from 'smag-commons/cfg/hierarchy/ec';
import {COSTS, EC_PLANTS_REI_REDISTRIBUTION, PLANT_TIP_QUOTE} from 'smag-commons/cfg/economics-values';
import {
    getCacv,
    getDedicatedShare,
    getPlantCacv,
    getTip,
    NamedECEconomicsThirdPartyProducerPod,
    showCacv,
    showDedicatedShare
} from '../../../shared/ec-members';


@Component({
    selector: 'app-economics-member-plants',
    templateUrl: './economics-member-plants.component.html',
    styleUrls: ['./economics-member-plants.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        {provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS}
    ]
})
export class EconomicsMemberPlantsComponent implements OnInit, OnDestroy {
    private destroyed = new Subject<void>();
    private nvp: NiceValuePipe;
    private communityNotConfigured = false;
    private timeZone: string = null;
    private cacvMode: ECCacvRedistributionMode = null;

    @ViewChild('ecWaitingForApproval') private ecWaitingForApprovalDialog: TemplateRef<any>;

    hierarchyEntity: Observable<HierarchyEntity> = null;
    billingPeriodCfg: BillingPeriodConfig = null;
    billingPeriod: BillingPeriod = {} as BillingPeriod;
    billingPeriodSubj: ReplaySubject<BillingPeriod> = new ReplaySubject<BillingPeriod>(1);
    pods: NamedECEconomicsThirdPartyProducerPod[] = null;
    currentPod: NamedECEconomicsThirdPartyProducerPod = null;
    displayedColumns: string[] = ['chevron', 'name', 'pod', 'tip'];
    dataSourceInfo: DataSourceInfo;
    templateIsDownloading = false;
    sharedConfig: SharedConfig;
    contentResize: Observable<void>;
    inactiveEc = false;
    futureEc = false;
    donutChart: {} = null;
    energySharing: ChartQuantity[] = null;
    isSingleMonth = false;
    isGSE = false;

    readonly PAGE_SIZE = DEFAULT_PAGE_SIZE;
    readonly PAGE_SIZES = DEFAULT_PAGE_SIZES;
    readonly EconomicsErrorType = EconomicsErrorType;
    readonly isDefined = isDefined;
    readonly hasError = hasError;
    readonly showError = showError;
    readonly getSelectionLineColor = getSelectionLineColor;
    readonly getTooltipBorderColor = getTooltipBorderColor;
    readonly getTooltipBackgroundColor = getTooltipBackgroundColor;
    readonly getGridColor = getGridColor;
    readonly CACV_STRING = CACV_STRING;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly router: Router,
                private readonly hierarchyService: HierarchyService,
                private readonly configurationService: ConfigurationService,
                private readonly apiService: SmagApiService,
                private readonly i18n: I18NService,
                private readonly resizeService: ResizeService,
                private readonly dialog: MatDialog) {
        this.nvp = new NiceValuePipe(i18n);
        this.sharedConfig = cfg.shared;
        this.contentResize = this.resizeService.observe(['content']).pipe(takeUntil(this.destroyed));
    }

    ngOnInit(): void {

        this.configurationService.timeConfig
            .pipe(first())
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);

        // as we store time range in URL, we need to avoid "navigation" to same entity when setting URL parameters for time-range
        this.hierarchyEntity = this.hierarchyService.selection.pipe(
            distinctUntilChanged((h1, h2) => h1?.id === h2?.id),
            shareReplay(1),
            takeUntil(this.destroyed)
        );

        this.hierarchyEntity.pipe(
            filter(hierarchyEntity => !!hierarchyEntity),
            switchMap(hierarchyEntity => can(hierarchyEntity.permissions, Permission.READ) ?
                this.apiService.getEntity(hierarchyEntity.id) :
                of(null)),
            takeUntil(this.destroyed)
        ).subscribe(entity => {
            this.billingPeriodCfg = {
                pastDate: entity ? (entity as EnergyCommunity).activation_date || null : null,
                futureDate: null
            };
            if (!isDefined(this.billingPeriodCfg.pastDate)) {
                this.inactiveEc = true;
                this.showWaitingForApprovalModal();
            } else {
                this.inactiveEc = false;
                if (this.billingPeriodCfg.pastDate > moment.tz(this.timeZone).valueOf()) {
                    this.futureEc = true;
                    this.showWaitingForApprovalModal();
                } else {
                    this.futureEc = false;
                }
            }
        });

        combineLatest([
            this.hierarchyEntity.pipe(filter(he => isEnergyCommunity(he))),
            this.billingPeriodSubj,
            this.configurationService.timeConfig
        ]).pipe(switchMap(([he, billingPeriod, timeCfg]) => forkJoin([
                this.apiService.ecEconomicsSummaryThirdParty(he.id,
                    sanityFromTimeActivationDate(billingPeriod.fromTime, this.billingPeriodCfg.pastDate, this.timeZone),
                    sanityToTime(timeCfg.time_zone, billingPeriod.toTime)),
                of(he),
                of(timeCfg.time_zone),
                this.apiService.getSubjectInfo().pipe(map(subjectInfo => subjectInfo.subject.pods)),
                of(billingPeriod)])),
            takeUntil(this.destroyed))
            .subscribe(([response, he, tz, memberPods, billingPeriod]) => {
                this.isSingleMonth = isSingleMonthSelected(billingPeriod as BillingPeriod, tz, this.showNullData);
                this.communityNotConfigured = (response.errors || [])
                    .filter(err => err.type === EconomicsErrorType.MISSING_PARAM)
                    .some(err =>
                        err.metadata[ECO_ERROR_PARAM_KEY] === COSTS ||
                        err.metadata[ECO_ERROR_PARAM_KEY] === EC_PLANTS_REI_REDISTRIBUTION ||
                        err.metadata[ECO_ERROR_PARAM_KEY] === PLANT_TIP_QUOTE);
                this.isGSE = response.gse;
                this.mkPods(response.pods, he, tz, memberPods);
                this.cacvMode = response.c_acv_mode;
            });

        this.hierarchyEntity.pipe(
            filter(he => he !== null && can(he.permissions, Permission.READ)),
            switchMap(he => this.apiService.communityPodsStatistics(he.id).pipe(
                map(stats => getDataSourceIconInfo(mkMode(stats))))),
            takeUntil(this.destroyed)
        ).subscribe(dataSourceIconInfo => {
            this.dataSourceInfo = dataSourceIconInfo;
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    showWaitingForApprovalModal() {
        this.dialog.open(this.ecWaitingForApprovalDialog, {
            maxWidth: `${DEFAULT_DIALOG_MAX_WIDTH}px`,
            minWidth: `${DEFAULT_DIALOG_MIN_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe();
    }

    onChangeBillingPeriod(period: BillingPeriod) {
        this.billingPeriodSubj.next(period);
        this.billingPeriod = period;
        this.router.navigate([],
            {
                queryParams: mkBillingPeriodParams(period),
                queryParamsHandling: 'merge'
            }).then();
    }

    onSelectPlant(pod: NamedECEconomicsThirdPartyProducerPod) {
        if (this.currentPod !== pod && pod.isActive) {
            this.currentPod = pod;
            this.mkCharts(this.currentPod);
            this.energySharing = this.mkEnergySharing(getTimelyData(this.currentPod));
        } else if (this.currentPod === pod && pod.isActive) {
            this.currentPod = null;
        }
    }

    isDetailViewable(entity: HierarchyEntity): boolean {
        return !!entity && can(entity.permissions, Permission.READ);
    }

    arePlantsActive(pod: NamedECEconomicsThirdPartyProducerPod) {
        return pod.namedPlants.map(plant => plant.isActive).reduce((acc, val) => acc && val, true);
    }

    get showNullData() {
        return this.inactiveEc || this.futureEc;
    }

    get keepYourSecrets() {
        return this.showNullData || this.communityNotConfigured;
    }

    get timeDelta() {
        return this.isSingleMonth ? TIME_DELTA_HOUR : TIME_DELTA_DAY;
    }

    get nonIncentivizedPlants() {
        return this.currentPod.plants.some(plant => plant.incentive_mode === PlantIncentiveMode.NO_INCENTIVE);
    }

    get showDedicatedShare() {
        return !this.keepYourSecrets && showDedicatedShare(this.currentPod);
    }

    get showCacv() {
        return showCacv(this.currentPod);
    }

    get tip() {
        return getTip(this.currentPod, this.nvp, this.i18n);
    }

    get cacv() {
        return getCacv(this.currentPod, this.cacvMode, this.i18n);
    }

    get plantCacv() {
        return getPlantCacv(this.currentPod, this.cacvMode, this.nvp);
    }

    get dedicatedShare() {
        return getDedicatedShare(this.currentPod, this.nvp);
    }

    private mkCharts(pod: NamedECEconomicsThirdPartyProducerPod) {
        this.donutChart = {
            chart: {
                type: 'donut',
                height: 100,
                offsetX: -100
            },
            colors: [environment.appColors.effectiveInjectedEnergy, environment.appColors.ecoEmpty],
            dataLabels: disableDataLabels(),
            apexLegends: disableLegend(),
            plotOptions: {
                pie: {
                    donut: {
                        size: '65%'
                    }
                }
            },
            series: pod.gse ?
                [pod.shared_energy_ratio * 100, (1 - pod.shared_energy_ratio) * 100] :
                [pod.estimated_shared_energy_ratio * 100, (1 - pod.estimated_shared_energy_ratio) * 100],
            tooltip: {enabled: false},
            title: this.mkChartTitle(this.nvp.transform((pod.gse ? pod.shared_energy_ratio : pod.estimated_shared_energy_ratio) * 100, '%', 1),
                environment.appColors.ecoDarkLegend)
        };
    }

    private mkChartTitle(text: string, color: string): ApexTitleSubtitle {
        return {
            offsetX: 200,
            offsetY: 20,
            floating: true,
            style: {
                fontSize: '28px',
                fontWeight: '700',
                fontFamily: environment.fontFamily,
                color
            },
            text
        };
    }

    private mkEnergySharing(timelyData: ECEconomicsThirdPartyProducerPodTimelyData): ChartQuantity[] {
        return timelyData ? this.cleanChartQuantities([
                {
                    name: this.i18n.translate('Sharing from injection'),
                    data: [
                        {
                            data: timelyData.times.map((t, i) => [t, timelyData.effective_injected_energy[i]]),
                            style: {
                                color: this.cfg.shared.colors.injectedPower,
                                strokeWidth: 3
                            },
                            name: this.i18n.translate('Effective injected energy')
                        },
                        {
                            data: timelyData.times.map((t, i) => [t, timelyData.shared_energy[i]]),
                            style: {
                                color: this.cfg.shared.colors.effectiveInjectedEnergy,
                                strokeWidth: 3
                            },
                            name: this.i18n.translate('Sharing from injection')
                        }
                    ],
                    uom: 'kWh',
                }])
            : [];
    }

    private cleanChartQuantities(chartQuantities: ChartQuantity[]) {
        return chartQuantities.map(qt => ({...qt, data: noData(qt.data) ? [] : qt.data}));
    }

    private isPodActive(pod: ECMemberPOD, tz: string): boolean {
        const hour = moment.tz(tz).startOf('hour').valueOf();
        return isDefined(pod.effective_date) && pod.effective_date <= hour && (!isDefined(pod.deactivation_date) || pod.deactivation_date > hour);
    }

    private isPlantActive(plant: EnergyCommunityNamedPlant, tz: string): boolean {
        const hour = moment.tz(tz).startOf('hour').valueOf();
        return isDefined(plant.plant.activation_date) && plant.plant.activation_date <= hour;
    }

    private mkPods(podsIn: ECEconomicsThirdPartyProducerPod[], he: HierarchyEntity, tz: string, memberPods: ECMemberPOD[]) {
        forkJoin(podsIn.map(podIn => this.getNamedPod(podIn, he, tz, memberPods)))
            .subscribe(podsOut => this.pods = podsOut);
    }

    private getNamedPod(pod: ECEconomicsThirdPartyProducerPod, he: HierarchyEntity, tz: string, memberPods: ECMemberPOD[]): Observable<NamedECEconomicsThirdPartyProducerPod> {
        return this.apiService.findEntityByPOD(pod.pod)
            .pipe(switchMap(resp => this.apiService.getECPod(he.id, resp.id)
                .pipe(map(ecNamedPod => ({
                    ...pod,
                    name: ecNamedPod.name,
                    namedPlants: ecNamedPod.plants.map(plant => ({
                        name: plant.name,
                        isActive: this.isPlantActive(plant, tz),
                        generatesTip: generatesTip(plant.plant)
                    })),
                    isActive: this.isPodActive(memberPods.find(p => p.pod === pod.pod), tz),
                    generatesTip: ecNamedPod.plants.some(plant => generatesTip(plant.plant))
                })))));
    }


}
